import { useEffect, useRef } from 'react';
import { logJourneyEvent } from '@/src/Stats/components/hooks/useShareJourney';

interface GalleryCTAProps {
    gallery: GalleryObject | null;
}

// Fixed bottom call-to-action bar shared by every gallery grid variant.
// Publishes its rendered height as --gallery-cta-height on <html> so the
// scroll container can reserve exactly enough room below the last photos.
export default function GalleryCTA({ gallery }: GalleryCTAProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const hasCTA = Boolean(gallery?.ctaImage || gallery?.ctaText);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const root = document.documentElement;
        const observer = new ResizeObserver(() => {
            root.style.setProperty(
                '--gallery-cta-height',
                `${container.offsetHeight}px`,
            );
        });
        observer.observe(container);
        return () => {
            observer.disconnect();
            root.style.removeProperty('--gallery-cta-height');
        };
    }, [hasCTA]);

    if (!hasCTA) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className="cta-container fixed bottom-0 left-0 right-0 flex justify-center items-center cursor-pointer clickable pb-[env(safe-area-inset-bottom)]"
            onClick={() => {
                if (gallery?.ctaLink) {
                    logJourneyEvent('cta-click', {
                        metadata: { link: gallery.ctaLink },
                    });
                    window.open(gallery?.ctaLink, '_blank');
                }
            }}
        >
            {gallery?.ctaImage && (
                <img
                    src={gallery?.ctaImage}
                    alt="Smilebooth CTA"
                    className="w-full h-auto max-h-[25vh] object-contain md:w-auto md:max-h-[100px]"
                />
            )}
            {gallery?.ctaText && (
                <p className="text-lg font-semibold text-center px-6 py-4 md:text-base md:py-2">
                    {gallery?.ctaText}
                </p>
            )}
        </div>
    );
}
